const DEFAULTS = {
  title: "Sign in to Veronese",
  subtitle: "Start calling now",
  tagline: "",
  emailPlaceholder: "Enter your email address",
  submitLabel: "Send Verification Code",
  resendLabel: "Send code again",
  verifyLabel: "Verify Code",
  codePlaceholder: "Enter 6-digit code",
  googleLabel: "Continue with Google",
  facebookLabel: "Continue with Facebook",
  xLabel: "Continue with X",
  successTitle: "Check your email",
  successIntro: "We sent a verification code to",
  companyLabel: "Or company sign up",
  helpLabel: "Need help signing in?",
  dividerLabel: "or"
}

function escapeHtml(value) {
  return String(value ?? "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#39;")
}

function booleanAttr(element, name, defaultValue = true) {
  if (!element.hasAttribute(name)) return defaultValue
  const value = (element.getAttribute(name) || "").toLowerCase()
  if (value === "" || value === "true" || value === "1" || value === "yes") return true
  if (value === "false" || value === "0" || value === "no") return false
  return defaultValue
}

function attrOrDefault(element, name, defaultValue) {
  return element.hasAttribute(name) ? (element.getAttribute(name) || "") : defaultValue
}

const HTMLElementBase = typeof HTMLElement === "undefined" ? class {} : HTMLElement

class MobayiloAuthPanel extends HTMLElementBase {
  constructor() {
    super()
    this._mounted = false
    this._currentEmail = ""
    this._emailInputId = `mb-auth-email-${Math.random().toString(36).slice(2, 10)}`
    this._codeInputId = `mb-auth-code-${Math.random().toString(36).slice(2, 10)}`
    this._sessionId = null
  }

  connectedCallback() {
    if (this._mounted) return
    this._mounted = true
    this.render()
    this.bindEvents()
  }

  get config() {
    return {
      title: this.getAttribute("title") || DEFAULTS.title,
      subtitle: this.getAttribute("subtitle") || DEFAULTS.subtitle,
      tagline: this.getAttribute("tagline") || DEFAULTS.tagline,
      emailPlaceholder: this.getAttribute("email-placeholder") || DEFAULTS.emailPlaceholder,
      submitLabel: this.getAttribute("submit-label") || DEFAULTS.submitLabel,
      resendLabel: this.getAttribute("resend-label") || DEFAULTS.resendLabel,
      verifyLabel: this.getAttribute("verify-label") || DEFAULTS.verifyLabel,
      codePlaceholder: this.getAttribute("code-placeholder") || DEFAULTS.codePlaceholder,
      googleLabel: this.getAttribute("google-label") || DEFAULTS.googleLabel,
      facebookLabel: this.getAttribute("facebook-label") || DEFAULTS.facebookLabel,
      xLabel: this.getAttribute("x-label") || DEFAULTS.xLabel,
      successTitle: this.getAttribute("success-title") || DEFAULTS.successTitle,
      successIntro: this.getAttribute("success-intro") || DEFAULTS.successIntro,
      dividerLabel: this.getAttribute("divider-label") || DEFAULTS.dividerLabel,
      companyLabel: attrOrDefault(this, "company-label", DEFAULTS.companyLabel),
      companyUrl: attrOrDefault(this, "company-url", "#"),
      helpLabel: attrOrDefault(this, "help-label", DEFAULTS.helpLabel),
      helpUrl: attrOrDefault(this, "help-url", "#"),
      showGoogle: booleanAttr(this, "show-google", true),
      showFacebook: booleanAttr(this, "show-facebook", true),
      showX: booleanAttr(this, "show-x", true)
    }
  }

  emit(name, detail = {}, cancelable = true) {
    return this.dispatchEvent(
      new CustomEvent(name, {
        bubbles: true,
        composed: true,
        cancelable,
        detail: { panel: this, ...detail }
      })
    )
  }

  setStatus(message, tone = "info") {
    const status = this.querySelector('[data-role="status"]')
    if (!status) return
    status.textContent = message || ""
    status.classList.remove("mb-auth-status--error", "mb-auth-status--info")
    if (message) status.classList.add(tone === "error" ? "mb-auth-status--error" : "mb-auth-status--info")
  }

  setSuccessStatus(message, tone = "info") {
    const status = this.querySelector('[data-role="success-status"]')
    if (!status) return
    status.textContent = message || ""
    status.classList.remove("mb-auth-status--error", "mb-auth-status--info")
    if (message) status.classList.add(tone === "error" ? "mb-auth-status--error" : "mb-auth-status--info")
  }

  getEmail() {
    const input = this.querySelector('[data-role="email"]')
    return (input?.value || "").trim()
  }

  setEmail(value) {
    const input = this.querySelector('[data-role="email"]')
    if (input) input.value = value || ""
    this._currentEmail = (value || "").trim()
  }

  getCode() {
    const input = this.querySelector('[data-role="code"]')
    return (input?.value || "").trim()
  }

  setCode(value) {
    const input = this.querySelector('[data-role="code"]')
    if (input) input.value = value || ""
  }

  setSessionId(value) {
    this._sessionId = value || null
  }

  getSessionId() {
    return this._sessionId
  }

  showLoading(message = "Sending verification code...") {
    this.setStatus(message, "info")
  }

  showError(message = "We couldn't send the verification code. Try again.") {
    this.setStatus(message, "error")
  }

  showSuccess(email = "") {
    const normalizedEmail = (email || this.getEmail() || this._currentEmail).trim()
    this._currentEmail = normalizedEmail
    const emailTarget = this.querySelector('[data-role="success-email"]')
    if (emailTarget) emailTarget.textContent = normalizedEmail

    const formPanel = this.querySelector('[data-role="form-panel"]')
    const successPanel = this.querySelector('[data-role="success-panel"]')
    if (!formPanel || !successPanel) return

    formPanel.classList.add("mb-auth-hidden")
    successPanel.classList.remove("mb-auth-hidden")
    this.setStatus("")
    this.setSuccessStatus("")
    this.setCode("")
  }

  reset() {
    const formPanel = this.querySelector('[data-role="form-panel"]')
    const successPanel = this.querySelector('[data-role="success-panel"]')
    if (!formPanel || !successPanel) return

    successPanel.classList.add("mb-auth-hidden")
    formPanel.classList.remove("mb-auth-hidden")
    this.setStatus("")
    this.setSuccessStatus("")
    this.setCode("")
    this.setSessionId(null)
  }

  resendSuccess(message = "Verification code sent again.") {
    this.setSuccessStatus(message, "info")
  }

  bindEvents() {
    const form = this.querySelector('[data-role="form"]')
    const resendButton = this.querySelector('[data-role="resend"]')
    const verifyButton = this.querySelector('[data-role="verify"]')
    const googleButton = this.querySelector('[data-role="google"]')
    const facebookButton = this.querySelector('[data-role="facebook"]')
    const xButton = this.querySelector('[data-role="x"]')
    const companyLink = this.querySelector('[data-role="company-link"]')
    const helpLink = this.querySelector('[data-role="help-link"]')

    form?.addEventListener("submit", (event) => {
      event.preventDefault()
      const email = this.getEmail()
      if (!email || !email.includes("@")) {
        this.showError("Enter a valid email address.")
        return
      }

      this._currentEmail = email
      this.showLoading()
      const shouldContinueDefault = this.emit("auth-panel:submit", { email })
      if (shouldContinueDefault) {
        this.showSuccess(email)
      }
    })

    resendButton?.addEventListener("click", (event) => {
      event.preventDefault()
      const email = this._currentEmail || this.getEmail()
      if (!email) {
        this.setSuccessStatus("Enter your email above first.", "error")
        return
      }

      this.setSuccessStatus("Sending new code...", "info")
      const shouldContinueDefault = this.emit("auth-panel:resend", { email, sessionId: this.getSessionId() })
      if (shouldContinueDefault) {
        this.resendSuccess()
      }
    })

    verifyButton?.addEventListener("click", (event) => {
      event.preventDefault()
      const code = this.getCode()
      if (!code) {
        this.setSuccessStatus("Code is required.", "error")
        return
      }

      this.setSuccessStatus("Verifying code...", "info")
      const shouldContinueDefault = this.emit("auth-panel:verify", {
        email: this._currentEmail || this.getEmail(),
        code,
        sessionId: this.getSessionId()
      })
      if (shouldContinueDefault) {
        this.setSuccessStatus("Code submitted.", "info")
      }
    })

    googleButton?.addEventListener("click", (event) => {
      event.preventDefault()
      const shouldContinueDefault = this.emit("auth-panel:google", {})
      if (!shouldContinueDefault) return
      const url = this.getAttribute("google-url")
      if (url) window.location.assign(url)
    })

    facebookButton?.addEventListener("click", (event) => {
      event.preventDefault()
      const shouldContinueDefault = this.emit("auth-panel:facebook", {})
      if (!shouldContinueDefault) return
      const url = this.getAttribute("facebook-url")
      if (url) window.location.assign(url)
    })

    xButton?.addEventListener("click", (event) => {
      event.preventDefault()
      const shouldContinueDefault = this.emit("auth-panel:x", {})
      if (!shouldContinueDefault) return
      const url = this.getAttribute("x-url")
      if (url) window.location.assign(url)
    })

    companyLink?.addEventListener("click", () => {
      this.emit("auth-panel:company-link", {}, false)
    })

    helpLink?.addEventListener("click", () => {
      this.emit("auth-panel:help-link", {}, false)
    })
  }

  render() {
    const {
      title,
      subtitle,
      tagline,
      emailPlaceholder,
      submitLabel,
      resendLabel,
      verifyLabel,
      codePlaceholder,
      googleLabel,
      facebookLabel,
      xLabel,
      successTitle,
      successIntro,
      dividerLabel,
      companyLabel,
      companyUrl,
      helpLabel,
      helpUrl,
      showGoogle,
      showFacebook,
      showX
    } = this.config

    const emailValue = escapeHtml(this.getAttribute("email") || "")
    const showSocial = showGoogle || showFacebook || showX
    const showCompanyLink = companyLabel.trim().length > 0 && companyUrl.trim().length > 0
    const showHelpLink = helpLabel.trim().length > 0 && helpUrl.trim().length > 0
    const showFooterLinks = showCompanyLink || showHelpLink

    this.innerHTML = `
      <div class="mb-auth-root">
        <div class="mb-auth-card" data-role="form-panel">
          <header class="mb-auth-center">
            <h2 class="mb-auth-title">${escapeHtml(title)}</h2>
            <p class="mb-auth-copy">${escapeHtml(subtitle)}</p>
            ${tagline.trim().length > 0 ? `<p class="mb-auth-copy">${escapeHtml(tagline)}</p>` : ""}
          </header>

          ${showGoogle ? `
            <button type="button" class="mb-auth-social" data-role="google">
              <svg class="mb-auth-google-icon" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05" />
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
              </svg>
              <span>${escapeHtml(googleLabel)}</span>
            </button>
          ` : ""}

          ${showFacebook ? `
            <button type="button" class="mb-auth-social mb-auth-social-facebook" data-role="facebook">
              <svg class="mb-auth-facebook-icon" viewBox="0 0 320 512" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                <path d="M279.14 288l14.22-92.66h-88.91v-60.13c0-25.35 12.42-50.06 52.24-50.06h40.42V6.26S260.43 0 225.36 0c-73.22 0-121.08 44.38-121.08 124.72v70.62H22.89V288h81.39v224h100.17V288z" />
              </svg>
              <span>${escapeHtml(facebookLabel)}</span>
            </button>
          ` : ""}

          ${showX ? `
            <button type="button" class="mb-auth-social" data-role="x">
              <svg class="mb-auth-x-icon" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                <path d="M18.901 1.153h3.68l-8.04 9.19L24 22.846h-7.406l-5.8-7.584-6.638 7.584H.474l8.6-9.83L0 1.154h7.594l5.243 6.932 6.064-6.932zm-1.292 19.494h2.039L6.486 3.24H4.298l13.311 17.407z" />
              </svg>
              <span>${escapeHtml(xLabel)}</span>
            </button>
          ` : ""}

          ${showSocial ? `
            <div class="mb-auth-divider">
              <span></span>
              <strong>${escapeHtml(dividerLabel)}</strong>
              <span></span>
            </div>
          ` : ""}

          <form data-role="form" class="mb-auth-form">
            <label class="mb-auth-label" for="${this._emailInputId}">Email</label>
            <input id="${this._emailInputId}" data-role="email" type="email" autocomplete="email" placeholder="${escapeHtml(emailPlaceholder)}" value="${emailValue}" />
            <button type="submit" class="mb-auth-submit">${escapeHtml(submitLabel)}</button>
          </form>

          <p class="mb-auth-status" data-role="status"></p>
        </div>

        <div class="mb-auth-card mb-auth-hidden" data-role="success-panel">
          <div class="mb-auth-center">
            <div class="mb-auth-success-icon" aria-hidden="true">
              <svg viewBox="0 0 256 256" xmlns="http://www.w3.org/2000/svg">
                <path d="M104 152a8 8 0 0 1-8 8H56a8 8 0 0 1 0-16h40a8 8 0 0 1 8 8Zm136-36v60a16 16 0 0 1-16 16H136v32a8 8 0 0 1-16 0v-32H32a16 16 0 0 1-16-16V116A60.07 60.07 0 0 1 76 56h76V24a8 8 0 0 1 8-8h32a8 8 0 0 1 0 16h-24V56h12A60.07 60.07 0 0 1 240 116ZM120 176V116a44 44 0 0 0-88 0v60Zm104-60a44.05 44.05 0 0 0-44-44h-12v72a8 8 0 0 1-16 0V72h-35.25A59.86 59.86 0 0 1 136 116v60h88Z" />
              </svg>
            </div>
            <h2 class="mb-auth-title">${escapeHtml(successTitle)}</h2>
            <p class="mb-auth-copy">
              ${escapeHtml(successIntro)}
              <strong data-role="success-email"></strong>.
            </p>
          </div>

          <div class="mb-auth-form">
            <label class="mb-auth-label" for="${this._codeInputId}">Verification code</label>
            <input id="${this._codeInputId}" data-role="code" type="text" inputmode="numeric" autocomplete="one-time-code" placeholder="${escapeHtml(codePlaceholder)}" />
            <button type="button" class="mb-auth-submit" data-role="verify">${escapeHtml(verifyLabel)}</button>
          </div>

          <p class="mb-auth-status" data-role="success-status"></p>
          <button type="button" class="mb-auth-link-button" data-role="resend">${escapeHtml(resendLabel)}</button>
        </div>

        ${showFooterLinks ? `
          <div class="mb-auth-links">
            ${showCompanyLink ? `<a href="${escapeHtml(companyUrl)}" data-role="company-link">${escapeHtml(companyLabel)}</a>` : ""}
            ${showHelpLink ? `<a href="${escapeHtml(helpUrl)}" data-role="help-link">${escapeHtml(helpLabel)}</a>` : ""}
          </div>
        ` : ""}
      </div>
    `
  }
}

if (typeof window !== "undefined" && window.customElements && !window.customElements.get("mobayilo-auth-panel")) {
  window.customElements.define("mobayilo-auth-panel", MobayiloAuthPanel)
}

export { MobayiloAuthPanel }
export default MobayiloAuthPanel
